import { timeZoneName, type ScheduleDay } from '@/packages/sxm/sports/schedule';
import type { ScheduleDriver, ScheduleEntity, ScheduleRow } from '@/packages/sxm/sports/scheduleItems';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { Text } from '@/ui-components/atoms/Text/Text';
import { ListenOptions } from '@/ui-components/molecules/ListenOptions/ListenOptions';
import { SportsListenLive } from '@/ui-components/molecules/SportsScheduleStrip/SportsListenLive';
import { SportsDriverChannels } from '@/ui-components/molecules/SportsScheduleStrip/SportsDriverChannels';
import { SportsScheduleGroup } from '@/ui-components/molecules/SportsScheduleStrip/SportsScheduleGroup';
import {
    SportsScheduleStrip,
    type SportsScheduleEntity,
} from '@/ui-components/molecules/SportsScheduleStrip/SportsScheduleStrip';
import styles from './sportschedule.module.scss';

const IMAGE_WIDTHS = { matchup: 32, event: 88 };

function toStripEntity(entity: ScheduleEntity, variant: keyof typeof IMAGE_WIDTHS): SportsScheduleEntity {
    return {
        name: entity.name,
        href: entity.href,
        description: entity.description,
        color: entity.color,
        image: entity.logo?.url && (
            <ImageWrapper src={entity.logo.url} alt="" optimizedWidth={IMAGE_WIDTHS[variant] * 2} />
        ),
        channels: entity.headline && (
            <ListenOptions entityName={entity.name} headline={entity.headline} options={entity.listenOptions} />
        ),
        listenAction: entity.playerUrl && <SportsListenLive name={entity.name} href={entity.playerUrl} />,
    };
}

function DriverChannels({ drivers }: { drivers: ScheduleDriver[] }) {
    if (!drivers.length) return undefined;

    const channels = drivers.map((driver) => ({ name: driver.name, channel: driver.channel, href: driver.deepLink }));
    return <SportsDriverChannels channels={channels} />;
}

function ScheduleStrip({ row }: { row: ScheduleRow }) {
    if (row.variant === 'event') {
        return (
            <SportsScheduleStrip
                variant="event"
                startTime={row.startTime}
                isLive={row.isLive}
                event={toStripEntity(row.event, 'event')}
                driverChannels={<DriverChannels drivers={row.drivers} />}
            />
        );
    }

    return (
        <SportsScheduleStrip
            variant="matchup"
            startTime={row.startTime}
            isLive={row.isLive}
            separator={row.separator}
            awayTeam={toStripEntity(row.awayTeam, 'matchup')}
            homeTeam={toStripEntity(row.homeTeam, 'matchup')}
        />
    );
}

export function ScheduleList({ days, timeZone }: { days: ScheduleDay<ScheduleRow>[]; timeZone: string }) {
    if (!days.length) return undefined;

    return (
        <div className={styles.days} data-cy="components-contentful-contextual-component-sports-dates">
            <Text tag="p" className={styles.timeZone}>{`All times listed ${timeZoneName(timeZone)}`}</Text>
            {days.map((day) => (
                <SportsScheduleGroup key={day.key} dateLabel={day.label}>
                    {day.items.map((row) => (
                        <div key={row.key} data-cy="molecules-sports-strip">
                            <ScheduleStrip row={row} />
                        </div>
                    ))}
                </SportsScheduleGroup>
            ))}
        </div>
    );
}
